// src/pages/pengamatan/buah-alami/PlotEditDialog.jsx
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Slide,
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';

// Transition untuk slide-in effect
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PlotEditDialog = ({
  open,
  selectedOrder,
  plotStatuses,
  onClose,
  onPlotStatusChange,
  onSave,
  currentTab,
  loading = false
}) => {
  // Fungsi untuk mendapatkan warna chip berdasarkan status
  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return 'success';
      case 'Processed':
        return 'info';
      case 'Pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Fungsi untuk mengurutkan plot secara ascending
  const sortPlotsAscending = (plots) => {
    if (!plots) return [];
    
    return plots.sort((a, b) => {
      const plotA = a.plot || '';
      const plotB = b.plot || '';
      
      if (!isNaN(plotA) && !isNaN(plotB)) {
        return parseInt(plotA) - parseInt(plotB);
      }
      
      return plotA.localeCompare(plotB);
    });
  };

  // FUNGSI UNTUK MENDAPATKAN PLOTS YANG DITAMPILKAN (SAMA SEPERTI TOOLTIP)
  const getPlotsToDisplay = () => {
    if (!selectedOrder || !selectedOrder.allPlots) return [];
    
    let filteredPlots = [];
    
    // FILTER SAMA PERSIS SEPERTI DI TOOLTIP
    switch (currentTab) {
      case 'Pending':
        filteredPlots = selectedOrder.allPlots.filter(plot => plot.status_pengamatan === 'Pending');
        break;
      case 'Approved':
        filteredPlots = selectedOrder.allPlots.filter(plot => plot.status_pengamatan === 'Approved');
        break;
      case 'Processed':
        filteredPlots = selectedOrder.allPlots.filter(plot => plot.status_pengamatan === 'Processed');
        break;
      case 'Closing':
        filteredPlots = selectedOrder.allPlots; // Tampilkan semua di tab Closing
        break;
      default:
        filteredPlots = selectedOrder.allPlots;
    }
    
    return sortPlotsAscending(filteredPlots);
  };

  // FUNGSI BARU: Mendapatkan opsi dropdown berdasarkan tab
  const getDropdownOptions = () => {
    switch (currentTab) {
      case 'Approved':
        // Di tab Approved: bisa ubah ke Draft atau Processed
        return [
          { value: 'Draft', label: 'Draft' },
          { value: 'Approved', label: 'Approved' },
          { value: 'Processed', label: 'Processed' }
        ];
      case 'Processed':
        // Di tab Processed: hanya bisa ubah ke Draft atau tetap Processed
        return [
          { value: 'Draft', label: 'Draft' },
          { value: 'Processed', label: 'Processed' }
        ];
      default:
        // Untuk tab lainnya, tampilkan semua opsi
        return [
          { value: 'Draft', label: 'Draft' },
          { value: 'Approved', label: 'Approved' },
          { value: 'Processed', label: 'Processed' }
        ];
    }
  };

  const plotsToDisplay = getPlotsToDisplay();
  const totalPlotCount = selectedOrder?.allPlots?.length || 0;
  const displayedPlotCount = plotsToDisplay.length;
  const dropdownOptions = getDropdownOptions(); // Dapatkan opsi dropdown

  // Hitung jumlah plot per status untuk info
  const getPlotCountByStatus = () => {
    if (!selectedOrder?.allPlots) return {};
    
    return {
      ongoing: selectedOrder.allPlots.filter(plot => plot.status_pengamatan === 'Ongoing').length,
      draft: selectedOrder.allPlots.filter(plot => plot.status_pengamatan === 'Draft').length,
      pending: selectedOrder.allPlots.filter(plot => plot.status_pengamatan === 'Pending').length,
      approved: selectedOrder.allPlots.filter(plot => plot.status_pengamatan === 'Approved').length,
      rejected: selectedOrder.allPlots.filter(plot => plot.status_pengamatan === 'Rejected').length,
      processed: selectedOrder.allPlots.filter(plot => plot.status_pengamatan === 'Processed').length
    };
  };

  const plotCounts = getPlotCountByStatus();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          minHeight: 'auto',
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" component="span">
            Edit <strong>{plotsToDisplay.length}</strong> Plot - Lokasi: {selectedOrder?.lokasi || 'Lokasi'}
          </Typography>
          {loading && (
            <CircularProgress size={20} />
          )}
        </Box>

        {/* Info Jumlah Plot - SAMA SEPERTI DI TOOLTIP sx={{ mb: 2, mt: 2 }} */}
        <Box sx={{ mt: 1 }}>
          <Alert severity="info" sx={{ mb: 1 }}>
            <Typography variant="body2">
              <strong>Info status pengamatan per plot: </strong>
              Ongoing: {plotCounts.ongoing} | Draft: {plotCounts.draft} | Pending: {plotCounts.pending} | Approved: {plotCounts.approved} | Rejected: {plotCounts.rejected} | Processed: {plotCounts.processed}
            </Typography>
          </Alert>
        </Box>

      </DialogTitle>

      <DialogContent sx={{paddingBottom: '0px'}}>

        {/* Tabel Plot - HANYA PLOT YANG SESUAI TAB */}
        <TableContainer 
          component={Paper} 
          variant="outlined"
          sx={{ 
            maxHeight: 400,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#c1c1c1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#a8a8a8',
            }
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', py: 1, backgroundColor: 'background.paper', width: '15%' }}>
                  Plot
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 1, backgroundColor: 'background.paper', width: '20%' }}>
                  Luas Plot
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 1, backgroundColor: 'background.paper', width: '20%' }}>
                  PJP
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 1, backgroundColor: 'background.paper', width: '20%' }}>
                  Status Saat Ini
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 1, backgroundColor: 'background.paper', width: '25%' }}>
                  Ubah Status
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plotsToDisplay.length > 0 ? (
                plotsToDisplay.map((plot) => (
                  <TableRow 
                    key={plot.plotKey || plot.plot}
                    sx={{ 
                      '&:last-child td, &:last-child th': { border: 0 },
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {plot.plot || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2">
                        {plot.luas_plot ? `${plot.luas_plot} ha` : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2">
                        {plot.pjp || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Chip 
                        label={plot.status_pengamatan || 'None'} 
                        color={getStatusColor(plot.status_pengamatan)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <FormControl fullWidth size="small">
                        <Select
                          value={plotStatuses[plot.plotKey || plot.plot] || plot.status_pengamatan || 'None'}
                          onChange={(e) => onPlotStatusChange(plot.plotKey || plot.plot, e.target.value)}
                          displayEmpty
                          size="small"
                        >
                          {/* GUNAKAN DROPDOWN OPTIONS YANG SUDAH DIFILTER */}
                          {dropdownOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      Tidak ada plot dengan status "{currentTab}"
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Total: {displayedPlotCount}/{totalPlotCount} plot
        </Typography>

      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={onClose} 
          color="inherit" // 
          disabled={loading}
        >
          Batal
        </Button>
        <Button 
          onClick={onSave} 
          variant="contained" 
          color="primary"
          disabled={plotsToDisplay.length === 0 || loading}
        >
          {loading ? 'Menyimpan...' : `Simpan Perubahan`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlotEditDialog;